import React from 'react'
import { Button, TextField, Stack, Typography, Box, Modal, styled, Grid } from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { useCreateClientMutation, useUpdateClientDetailsMutation } from '../../redux/api/clientApi'
import Select, { StylesConfig } from 'react-select'
import makeAnimated from 'react-select/animated'

const Item = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}))

const asteriskStyle = {
  color: 'red',
  marginLeft: '2px',
}
interface DealerGroup {
  dealerId: string
  dealerGroupName: string
  dealerGroupNumber: string
  dealerGroup?: string
}

interface Props {
  client?: any
  onClose: () => void
  refetch: () => void
  open: boolean
  dealerGroupNumberList?: DealerGroup[]
}

const phoneRegex = /^\+\d{1,3}-\d{3}-\d{3}-\d{4}$|^\+\d{1,3}-\d{4}-\d{4}$|^\+\d{1,3}\d{10}$/
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const validationSchema = yup.object({
  client: yup.string().required('Client is required'),
  dealerGroupNumber: yup.string().required('Dealer Group Number is required'),
})

const customStyles: StylesConfig<any, false> = {
  menu: (provided) => ({
    ...provided,
    position: 'absolute',
    zIndex: 9999,
  }),
  control: (baseStyles) => ({
    ...baseStyles,
    height: '56px',
  }),
}

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    height: 'auto',
    overflow: 'auto',
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    mb: 2,
  },
  noMargin: {
    margin: '0px!important',
  },
  alignItemsCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  clickable: {
    cursor: 'pointer',
  },
  fontStyleForm: {
    fontSize: '1rem',
  },
}

const animatedComponents = makeAnimated()

export const ClientDetailForm = ({
  onClose,
  refetch,
  open,
  client,
  dealerGroupNumberList = [],
}: Props) => {
  const [createClient, { isLoading, isError, error, isSuccess }] = useCreateClientMutation()
  const [
    updateClient,
    { isLoading: uIsLoading, isError: uIsError, error: uError, isSuccess: uIsSuccess },
  ] = useUpdateClientDetailsMutation()

  const dealerOptions = dealerGroupNumberList.map((group) => ({
    value: group.dealerId,
    label: group.dealerGroup || group.dealerGroupName,
    dealerGroupNumber: group.dealerGroupNumber,
  }))

  const formik = useFormik({
    initialValues: {
      client: client?.client ?? '',
      emailDomain: client?.emailDomain ?? '',
      twilioFromPhone: client?.twilioFromPhone ?? '',
      twilioAcctSid: client?.twilioAccountSid ?? '',
      loadSchema: client?.loadSchema ?? '',
      loadArchiveSchema: client?.loadArchiveSchema ?? '',
      noReplyEmail: client?.noReplyEmail ?? '',
      sendgridTemplate: client?.sendGridTemplate ?? '',
      payStoreUrl: client?.payStoreUrl ?? '',
      solicitationPhone: client?.customerServicePhone ?? '',
      schema: client?.schema ?? '',
      dealerGroupNumber: client?.dealerGroupNumber ?? '',
      dealerId: client?.dealerId ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      const isUpdating = !!client?.clientId
      const requestData = {
        client: values.client,
        emailDomain: values.emailDomain,
        twilioFromPhone: values.twilioFromPhone,
        twilioAcctSid: values.twilioAcctSid,
        loadSchema: values.loadSchema,
        loadArchiveSchema: values.loadArchiveSchema,
        noReplyEmail: values.noReplyEmail,
        sendgridTemplate: values.sendgridTemplate,
        payStoreUrl: values.payStoreUrl,
        solicitationPhone: values.solicitationPhone,
        schema: values.schema,
        dealerGroupNumber: values.dealerGroupNumber,
      }

      if (isUpdating) {
        updateClient({ clientId: client.clientId, ...requestData })
      } else {
        createClient(requestData)
      }
    },
  })

  React.useEffect(() => {
    if (isSuccess || uIsSuccess) {
      toast.success('Success!')
      formik.resetForm()
      onClose()
      refetch()
    }
    if (isError || uIsError) {
      toast.error(
        (error as any)?.data?.message ?? (uError as any)?.data?.message ?? 'Something Failed!',
        {
          position: 'top-right',
        },
      )
    }
  }, [isLoading, uIsLoading])

  React.useEffect(() => {
    formik.setValues({
      client: client?.client ?? '',
      emailDomain: client?.emailDomain ?? '',
      twilioFromPhone: client?.twilioFromPhone ?? '',
      twilioAcctSid: client?.twilioAccountSid ?? '',
      loadSchema: client?.schemaLoad ?? '',
      loadArchiveSchema: client?.schemaLoadArchive ?? '',
      noReplyEmail: client?.noReplyEmail ?? '',
      sendgridTemplate: client?.sendGridTemplate ?? '',
      payStoreUrl: client?.payStoreUrl ?? '',
      solicitationPhone: client?.customerServicePhone ?? '',
      schema: client?.schema ?? '',
      dealerGroupNumber: client?.dealerGroupNumber ?? '',
      dealerId: client?.dealerId ?? '',
    })
  }, [client])

  const reset = () => {
    formik.resetForm()
    onClose()
  }

  const selectedOption = formik.values.dealerId
    ? dealerOptions.find((option) => option.value === formik.values.dealerId)
    : dealerOptions.find((option) => option.dealerGroupNumber === formik.values.dealerGroupNumber)
  const renderRequiredLabel = (labelText: string) => (
    <span>
      {labelText} <span style={asteriskStyle}>*</span>
    </span>
  )
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='white'>
            Aftermarket Client Detail
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Stack spacing={2}>
                  <Item>
                    <TextField
                      fullWidth
                      id='client'
                      name='client'
                      label={renderRequiredLabel('Client')}
                      autoComplete='off'
                      value={formik.values.client}
                      onChange={formik.handleChange}
                      error={formik.touched.client && Boolean(formik.errors.client)}
                      helperText={
                        formik.touched.client && typeof formik.errors.client === 'string'
                          ? formik.errors.client
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='emailDomain'
                      name='emailDomain'
                      label='Email Domain'
                      autoComplete='off'
                      value={formik.values.emailDomain}
                      onChange={formik.handleChange}
                      error={formik.touched.emailDomain && Boolean(formik.errors.emailDomain)}
                      helperText={
                        formik.touched.emailDomain && typeof formik.errors.emailDomain === 'string'
                          ? formik.errors.emailDomain
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='twilioFromPhone'
                      name='twilioFromPhone'
                      label='Twilio From Phone'
                      autoComplete='off'
                      value={formik.values.twilioFromPhone}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.twilioFromPhone && Boolean(formik.errors.twilioFromPhone)
                      }
                      helperText={
                        formik.touched.twilioFromPhone &&
                        typeof formik.errors.twilioFromPhone === 'string'
                          ? formik.errors.twilioFromPhone
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='twilioAcctSid'
                      name='twilioAcctSid'
                      label='Twilio Acct Sid'
                      autoComplete='off'
                      value={formik.values.twilioAcctSid}
                      onChange={formik.handleChange}
                      error={formik.touched.twilioAcctSid && Boolean(formik.errors.twilioAcctSid)}
                      helperText={
                        formik.touched.twilioAcctSid &&
                        typeof formik.errors.twilioAcctSid === 'string'
                          ? formik.errors.twilioAcctSid
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='loadSchema'
                      name='loadSchema'
                      label='Load Schema'
                      autoComplete='off'
                      value={formik.values.loadSchema}
                      onChange={formik.handleChange}
                      error={formik.touched.loadSchema && Boolean(formik.errors.loadSchema)}
                      helperText={
                        formik.touched.loadSchema && typeof formik.errors.loadSchema === 'string'
                          ? formik.errors.loadSchema
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='noReplyEmail'
                      name='noReplyEmail'
                      label='No Reply Email'
                      autoComplete='off'
                      value={formik.values.noReplyEmail}
                      onChange={formik.handleChange}
                      error={formik.touched.noReplyEmail && Boolean(formik.errors.noReplyEmail)}
                      helperText={
                        formik.touched.noReplyEmail &&
                        typeof formik.errors.noReplyEmail === 'string'
                          ? formik.errors.noReplyEmail
                          : undefined
                      }
                    />
                  </Item>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack spacing={2}>
                  <Item>
                    <TextField
                      fullWidth
                      id='sendgridTemplate'
                      name='sendgridTemplate'
                      label='Sendgrid Template'
                      autoComplete='off'
                      value={formik.values.sendgridTemplate}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.sendgridTemplate && Boolean(formik.errors.sendgridTemplate)
                      }
                      helperText={
                        formik.touched.sendgridTemplate &&
                        typeof formik.errors.sendgridTemplate === 'string'
                          ? formik.errors.sendgridTemplate
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='payStoreUrl'
                      name='payStoreUrl'
                      label='Pay Store Url'
                      autoComplete='off'
                      value={formik.values.payStoreUrl}
                      onChange={formik.handleChange}
                      error={formik.touched.payStoreUrl && Boolean(formik.errors.payStoreUrl)}
                      helperText={
                        formik.touched.payStoreUrl && typeof formik.errors.payStoreUrl === 'string'
                          ? formik.errors.payStoreUrl
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='solicitationPhone'
                      name='solicitationPhone'
                      label='Solicitation Phone'
                      autoComplete='off'
                      value={formik.values.solicitationPhone}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.solicitationPhone && Boolean(formik.errors.solicitationPhone)
                      }
                      helperText={
                        formik.touched.solicitationPhone &&
                        typeof formik.errors.solicitationPhone === 'string'
                          ? formik.errors.solicitationPhone
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='loadArchiveSchema'
                      name='loadArchiveSchema'
                      label='Load Archive Schema'
                      autoComplete='off'
                      value={formik.values.loadArchiveSchema}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.loadArchiveSchema && Boolean(formik.errors.loadArchiveSchema)
                      }
                      helperText={
                        formik.touched.loadArchiveSchema &&
                        typeof formik.errors.loadArchiveSchema === 'string'
                          ? formik.errors.loadArchiveSchema
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <TextField
                      fullWidth
                      id='schema'
                      name='schema'
                      label='Schema'
                      autoComplete='off'
                      value={formik.values.schema}
                      onChange={formik.handleChange}
                      error={formik.touched.schema && Boolean(formik.errors.schema)}
                      helperText={
                        formik.touched.schema && typeof formik.errors.schema === 'string'
                          ? formik.errors.schema
                          : undefined
                      }
                    />
                  </Item>
                  <Item>
                    <Select
                      closeMenuOnSelect
                      components={animatedComponents}
                      placeholder={renderRequiredLabel('Select Dealer Group')}
                      value={selectedOption || null}
                      onChange={(selectedOption: any) => {
                        if (selectedOption) {
                          formik.setFieldValue('dealerId', selectedOption.value)
                          formik.setFieldValue(
                            'dealerGroupNumber',
                            selectedOption.dealerGroupNumber,
                          )
                        } else {
                          formik.setFieldValue('dealerId', '')
                          formik.setFieldValue('dealerGroupNumber', '')
                        }
                      }}
                      options={dealerOptions}
                      styles={customStyles}
                      isClearable
                      isLoading={!dealerGroupNumberList.length}
                    />
                    {formik.touched.dealerGroupNumber && formik.errors.dealerGroupNumber && (
                      <Typography color='error' variant='caption'>
                        {typeof formik.errors.dealerGroupNumber === 'string'
                          ? formik.errors.dealerGroupNumber
                          : undefined}
                      </Typography>
                    )}
                  </Item>
                </Stack>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'right', mt: '30px' }}>
              <Button
                variant='outlined'
                size='large'
                sx={{ mr: 2, borderRadius: '20px', textDecoration: 'none' }}
                color='error'
                onClick={reset}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                sx={{ borderRadius: '20px', textDecoration: 'none' }}
                size='large'
                type='submit'
                disabled={isLoading || uIsLoading}
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  )
}
